import { matchesTokens } from "@/lib/search";
import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { formatIDR } from "@/lib/format";
import { Card, PageHeader, Badge, Input, Select, Modal, Button } from "@/components/ui-kit";
import { DatePicker } from "@/components/DatePicker";
import { FileText, Download, Search, Calendar, CheckCircle2, Clock, AlertCircle, ExternalLink } from "lucide-react";

/**
 * Iter v34{af} — ClientInvoices
 * Read-only invoice list for `account_type=klien` accounts. Displays:
 *   • Filterable list of invoices assigned to the client
 *   • Payment status (paid / partial / unpaid) computed from paid_amount vs grand_total
 *   • Download PDF (invoice + kwitansi, ownership-checked by backend)
 */
export default function ClientInvoices() {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all"); // all | paid | partial | unpaid | overdue
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    setLoading(true);
    api.get("/client/invoices").then((r) => setInvoices(r.data || []))
      .catch((e) => toast.error(e?.response?.data?.detail || "Gagal memuat invoice"))
      .finally(() => setLoading(false));
  }, []);

  const enriched = useMemo(() => {
    const today = new Date();
    return invoices.map((inv) => {
      const grand = Number(inv.grand_total || 0);
      const paid = Number(inv.paid_amount || 0);
      const outstanding = Math.max(0, grand - paid);
      let status = "unpaid";
      if (paid <= 0) status = "unpaid";
      else if (paid >= grand - 0.5) status = "paid";
      else status = "partial";
      const due = inv.due_date ? new Date(inv.due_date) : null;
      const overdue = status !== "paid" && due && due < today;
      return { ...inv, _outstanding: outstanding, _status: status, _overdue: overdue };
    });
  }, [invoices]);

  const filtered = useMemo(() => {
    let out = enriched;
    const q = search.trim().toLowerCase();
    if (q) {
      // Iter v34cw (#5) — pencarian kombinasi kata.
      out = out.filter((i) => matchesTokens(
        `${i.invoice_number || ""} ${i.po_number || ""} ${i.recap_title || ""} ${i.invoice_date || ""} ${i._status || ""} ${(i.items || []).map((it) => it.material_name || "").join(" ")}`,
        q,
      ));
    }
    if (statusFilter === "overdue") out = out.filter((i) => i._overdue);
    else if (statusFilter !== "all") out = out.filter((i) => i._status === statusFilter);
    // Iter v34bv (#11) — filter tanggal invoice
    if (startDate) out = out.filter((i) => (i.invoice_date || (i.created_at || "").slice(0, 10)) >= startDate);
    if (endDate) out = out.filter((i) => (i.invoice_date || (i.created_at || "").slice(0, 10)) <= endDate);
    return out;
  }, [enriched, search, statusFilter, startDate, endDate]);

  const totals = useMemo(() => {
    const g = enriched.reduce((a, i) => a + Number(i.grand_total || 0), 0);
    const p = enriched.reduce((a, i) => a + Number(i.paid_amount || 0), 0);
    return { grand: g, paid: p, outstanding: Math.max(0, g - p) };
  }, [enriched]);

  const downloadPdf = async (inv) => {
    try {
      const r = await api.get(`/client/invoices/${inv.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `invoice_${(inv.invoice_number || inv.id).replace(/\//g, "_")}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal mengunduh PDF");
    }
  };

  const openDetail = async (inv) => {
    try {
      const r = await api.get(`/client/invoices/${inv.id}`);
      setDetail(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal memuat detail");
    }
  };

  return (
    <div className="min-h-full bg-[#fafaf5]">
      <div className="max-w-[1400px] mx-auto p-4 md:p-6 lg:p-8 space-y-6">
        <PageHeader
          overline="Klien · Invoice"
          title="Invoice Saya"
          description="Daftar tagihan (invoice) untuk perusahaan Anda. Klik unduh untuk salinan PDF resmi."
        />

        {/* KPI strip */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Card className="p-5" data-testid="client-inv-kpi-total">
            <p className="text-[10px] uppercase tracking-widest text-stone-500 font-semibold">Total Tagihan</p>
            <p className="mt-1 text-2xl font-mono-num font-bold text-stone-900">{formatIDR(totals.grand)}</p>
            <p className="text-[11px] text-stone-500 mt-1">{enriched.length} invoice</p>
          </Card>
          <Card className="p-5" data-testid="client-inv-kpi-paid">
            <p className="text-[10px] uppercase tracking-widest text-emerald-700 font-semibold">Sudah Dibayar</p>
            <p className="mt-1 text-2xl font-mono-num font-bold text-emerald-700">{formatIDR(totals.paid)}</p>
            <p className="text-[11px] text-stone-500 mt-1">Lunas + Cicilan</p>
          </Card>
          <Card className="p-5" data-testid="client-inv-kpi-outstanding">
            <p className="text-[10px] uppercase tracking-widest text-amber-700 font-semibold">Outstanding</p>
            <p className="mt-1 text-2xl font-mono-num font-bold text-amber-700">{formatIDR(totals.outstanding)}</p>
            <p className="text-[11px] text-stone-500 mt-1">Total belum dibayar</p>
          </Card>
        </div>

        {/* Filter bar */}
        <Card className="p-4 flex flex-col md:flex-row gap-3 md:items-center flex-wrap">
          <div className="flex-1 min-w-[200px] relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-stone-400" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} className="!pl-9" placeholder="Cari no invoice / PO / material…" data-testid="client-inv-search" />
          </div>
          <Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="md:!w-48" data-testid="client-inv-status-filter">
            <option value="all">Semua Status</option>
            <option value="paid">Lunas</option>
            <option value="partial">Sebagian</option>
            <option value="unpaid">Belum Bayar</option>
            <option value="overdue">Jatuh Tempo</option>
          </Select>
          <div className="w-[150px]"><DatePicker value={startDate} onChange={setStartDate} placeholder="Dari tanggal" data-testid="client-inv-start-date" /></div>
          <div className="w-[150px]"><DatePicker value={endDate} onChange={setEndDate} placeholder="Sampai tanggal" data-testid="client-inv-end-date" /></div>
          {(search || statusFilter !== "all" || startDate || endDate) && (
            <Button variant="ghost" onClick={() => { setSearch(""); setStatusFilter("all"); setStartDate(""); setEndDate(""); }} data-testid="client-inv-reset-filter">Reset</Button>
          )}
          <span className="text-xs text-stone-500 whitespace-nowrap">{filtered.length} / {enriched.length}</span>
        </Card>

        {/* Invoice table */}
        <Card className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-stone-50 border-b border-stone-200 text-left text-[10px] font-semibold uppercase tracking-wider text-stone-600">
                <th className="py-3 px-4">No Invoice</th>
                <th className="py-3 px-4">No PO</th>
                <th className="py-3 px-4">Tanggal</th>
                <th className="py-3 px-4">Jatuh Tempo</th>
                <th className="py-3 px-4 text-right">Total</th>
                <th className="py-3 px-4 text-right">Outstanding</th>
                <th className="py-3 px-4">Status</th>
                <th className="py-3 px-4 text-right">Aksi</th>
              </tr>
            </thead>
            <tbody data-testid="client-inv-tbody">
              {loading ? (
                <tr><td colSpan={8} className="py-10 text-center text-stone-500">Memuat…</td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={8} className="py-10 text-center text-stone-400">Tidak ada invoice.</td></tr>
              ) : filtered.map((inv, idx) => {
                const tone = inv._status === "paid" ? "moss" : inv._overdue ? "danger" : inv._status === "partial" ? "amber" : "stone";
                const Icon = inv._status === "paid" ? CheckCircle2 : inv._overdue ? AlertCircle : Clock;
                const label = inv._status === "paid" ? "Lunas" : inv._overdue ? "Jatuh Tempo" : inv._status === "partial" ? "Sebagian" : "Belum Bayar";
                return (
                  <tr key={inv.id} className="border-b border-stone-100 hover:bg-stone-50/70" data-testid={`client-inv-row-${idx}`}>
                    <td className="py-3 px-4 font-mono text-stone-800">{inv.invoice_number || "—"}</td>
                    <td className="py-3 px-4 font-mono text-stone-600">{inv.po_number || "—"}</td>
                    <td className="py-3 px-4 text-stone-600 text-xs">
                      <Calendar className="inline h-3 w-3 mr-1" />{(inv.invoice_date || "").slice(0, 10)}
                    </td>
                    <td className="py-3 px-4 text-stone-600 text-xs">
                      {(inv.due_date || "").slice(0, 10) || "—"}
                    </td>
                    <td className="py-3 px-4 text-right font-mono-num font-medium">{formatIDR(inv.grand_total)}</td>
                    <td className="py-3 px-4 text-right font-mono-num">{formatIDR(inv._outstanding)}</td>
                    <td className="py-3 px-4">
                      <Badge tone={tone}><Icon className="inline h-3 w-3 mr-1" />{label}</Badge>
                    </td>
                    <td className="py-3 px-4 text-right space-x-2 whitespace-nowrap">
                      <button onClick={() => openDetail(inv)} className="text-stone-500 hover:text-terra-700 inline-flex items-center gap-1 text-xs" data-testid={`client-inv-detail-${idx}`}>
                        <ExternalLink className="h-3 w-3" /> Detail
                      </button>
                      <button onClick={() => downloadPdf(inv)} className="text-stone-500 hover:text-emerald-700 inline-flex items-center gap-1 text-xs" data-testid={`client-inv-pdf-${idx}`}>
                        <Download className="h-3 w-3" /> PDF
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Card>

        {detail && (
          <Modal open onClose={() => setDetail(null)} title={`Invoice ${detail.invoice_number || ""}`} size="lg">
            <div className="space-y-4 text-sm" data-testid="client-inv-detail-modal">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <p className="text-[10px] uppercase text-stone-500 font-semibold">No PO</p>
                  <p className="font-mono">{detail.po_number || "—"}</p>
                </div>
                <div>
                  <p className="text-[10px] uppercase text-stone-500 font-semibold">Tanggal</p>
                  <p>{(detail.invoice_date || "").slice(0, 10)}</p>
                </div>
                <div>
                  <p className="text-[10px] uppercase text-stone-500 font-semibold">Jatuh Tempo</p>
                  <p>{(detail.due_date || "").slice(0, 10) || "—"}</p>
                </div>
                <div>
                  <p className="text-[10px] uppercase text-stone-500 font-semibold">Grand Total</p>
                  <p className="font-mono-num font-bold">{formatIDR(detail.grand_total)}</p>
                </div>
              </div>
              <div>
                <p className="text-[10px] uppercase text-stone-500 font-semibold mb-1">Item</p>
                <div className="border border-stone-200">
                  <table className="w-full text-xs">
                    <thead className="bg-stone-50">
                      <tr>
                        <th className="px-2 py-2 text-left">Material</th>
                        <th className="px-2 py-2 text-right">Qty</th>
                        <th className="px-2 py-2 text-right">Harga</th>
                        <th className="px-2 py-2 text-right">Subtotal</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(detail.items || []).map((it, i) => (
                        <tr key={i} className="border-t border-stone-100">
                          <td className="px-2 py-1.5">{it.material_name}</td>
                          <td className="px-2 py-1.5 text-right font-mono-num">{it.quantity} {it.unit}</td>
                          <td className="px-2 py-1.5 text-right font-mono-num">{formatIDR(it.unit_price)}</td>
                          <td className="px-2 py-1.5 text-right font-mono-num font-medium">{formatIDR(Number(it.quantity) * Number(it.unit_price))}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              <div className="flex justify-end pt-2">
                <button onClick={() => downloadPdf(detail)} className="inline-flex items-center gap-2 bg-stone-900 text-white px-4 py-2 text-xs hover:bg-stone-700" data-testid="client-inv-detail-pdf">
                  <Download className="h-4 w-4" /> Unduh PDF
                </button>
              </div>
            </div>
          </Modal>
        )}
      </div>
    </div>
  );
}
